import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Smartphone, Bell, BellRing, Check, TestTube2, Info, ShieldAlert, LogOut } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/** Settings page — install-to-home-screen + browser notifications control. */
export default function SettingsPage() {
  // --- Add to Home Screen ---
  const [installPrompt, setInstallPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const isIOS = /iP(hone|od|ad)/.test(navigator.userAgent);
  const isStandalone =
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    // eslint-disable-next-line
    (window.navigator).standalone === true;

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", () => setInstalled(true));
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, []);

  const install = async () => {
    if (!installPrompt) {
      toast.info(isIOS
        ? "On iPhone/iPad: tap the Share icon (square with arrow up) at the bottom of Safari, then choose 'Add to Home Screen'."
        : "Your browser hasn't offered install yet. Try opening this page in Chrome or Edge, or check the address-bar menu.");
      return;
    }
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === "accepted") { toast.success("Patient Access installed"); setInstalled(true); }
    setInstallPrompt(null);
  };

  // --- Browser notifications ---
  const supportsNotifications = typeof window !== "undefined" && "Notification" in window;
  const [permission, setPermission] = useState(supportsNotifications ? Notification.permission : "unsupported");
  const inIframe = typeof window !== "undefined" && window.self !== window.top;
  const isSecure = typeof window !== "undefined" && window.isSecureContext;
  const iosNeedsInstall = isIOS && !isStandalone && !supportsNotifications;

  // Detect which browser we're in — used to point the user at the exact settings page.
  const browserInfo = React.useMemo(() => {
    const ua = navigator.userAgent || "";
    if (/CriOS|Chrome/.test(ua) && !/Edg/.test(ua)) return { name: "Chrome",  steps: 'Tap the lock icon in the address bar → Site settings → Notifications → Allow.' };
    if (/Edg\//.test(ua))                          return { name: "Edge",    steps: 'Tap the lock icon in the address bar → Permissions for this site → Notifications → Allow.' };
    if (/Firefox/.test(ua))                        return { name: "Firefox", steps: 'Tap the shield/lock icon → Clear cookies and site data, or Permissions → Send Notifications → Allow.' };
    if (/Safari/.test(ua))                         return { name: "Safari",  steps: 'Safari → Settings → Websites → Notifications → find this site → Allow.' };
    return { name: "your browser", steps: "Open site settings for this page and set Notifications to Allow." };
  }, []);

  const enableNotifications = async () => {
    if (!supportsNotifications) {
      if (iosNeedsInstall) {
        toast.error("On iPhone/iPad you have to Add to Home Screen first, then open the app from your home screen — the notification prompt only works there.");
      } else {
        toast.error("This browser doesn't support notifications.");
      }
      return;
    }
    if (inIframe) {
      toast.error("This page is running inside a frame — notifications can only be enabled on the main site. Open the app in its own tab and try again.");
      return;
    }
    if (!isSecure) {
      toast.error("Notifications require an https:// URL. Open the app on its normal address (not http).");
      return;
    }
    if (Notification.permission === "denied") {
      toast.error(`${browserInfo.name} is blocking notifications for this site. ${browserInfo.steps}`, { duration: 9000 });
      return;
    }
    try {
      const p = await Notification.requestPermission();
      setPermission(p);
      if (p === "granted") {
        toast.success("Notifications enabled");
        try {
          new Notification("Patient Access", {
            body: "You'll be alerted when shifts, requests or reminders arrive.",
            icon: "/icon-192.png",
            badge: "/icon-192.png",
          });
        } catch {/* some browsers throw when tab is inactive — ignore */}
      } else if (p === "denied") {
        toast.error(`${browserInfo.name} blocked the request. ${browserInfo.steps}`, { duration: 9000 });
      } else {
        // "default" means the user dismissed the prompt without deciding — tell them.
        toast("Prompt was dismissed. Tap Enable again to see it.", { duration: 5000 });
      }
    } catch (e) {
      toast.error(`Couldn't ask for permission — ${e?.message || "unknown browser error"}`);
    }
  };

  const sendTestNotification = () => {
    if (permission !== "granted") { toast.error("Enable notifications first."); return; }
    new Notification("🧪 Test — Patient Access", {
      body: "If you can see this, notifications are working. Real alerts will fire when open shifts drop, requests get decisions, or your shift is about to start.",
      icon: "/icon-192.png",
      badge: "/icon-192.png",
      tag: "patient-access-test",
    });
    toast.success("Test notification sent");
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="settings-page">
      {/* Add to Home Screen */}
      <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="settings-install-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-100 grid place-items-center text-blue-700 shrink-0">
            <Smartphone size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-lg text-slate-900">Add to Home Screen</div>
            <div className="text-sm text-slate-600 mt-0.5">
              Install Patient Access on your phone or desktop so it opens like a native app — full-screen, no browser chrome, and it shows up in your app switcher.
            </div>
            {isStandalone || installed ? (
              <div className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-1.5 text-sm font-semibold">
                <Check size={14} /> Installed
              </div>
            ) : (
              <div className="mt-3 flex flex-wrap gap-2">
                <Button className="bg-blue-600 hover:bg-blue-700" onClick={install} data-testid="settings-install-btn">
                  <Smartphone size={14} className="mr-1" /> Install on this device
                </Button>
                {isIOS && (
                  <div className="text-xs text-slate-500 flex items-center gap-1">
                    <Info size={12} /> On iPhone: use Safari → Share → "Add to Home Screen"
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Notifications */}
      <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="settings-notif-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-fuchsia-100 grid place-items-center text-fuchsia-700 shrink-0">
            <Bell size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-lg text-slate-900">Notifications</div>
            <div className="text-sm text-slate-600 mt-0.5">
              Get notified about open shifts, PTO / trade / pickup approvals & denials, offered shifts, and a friendly reminder ~2 hours before your shift starts.
            </div>

            <div className="mt-3 flex flex-wrap gap-2 items-center">
              {permission === "granted" ? (
                <span className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-1.5 text-sm font-semibold">
                  <Check size={14} /> Enabled
                </span>
              ) : (
                <Button className="bg-fuchsia-600 hover:bg-fuchsia-700" onClick={enableNotifications} data-testid="settings-notif-enable-btn">
                  <BellRing size={14} className="mr-1" /> Enable notifications
                </Button>
              )}
              <Button variant="outline" onClick={sendTestNotification} disabled={permission !== "granted"} data-testid="settings-notif-test-btn">
                <TestTube2 size={14} className="mr-1" /> Send test notification
              </Button>
            </div>

            {/* Diagnostic banners — tell the user WHY the button might do nothing before they click it. */}
            {iosNeedsInstall && (
              <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-3 text-[13px] text-blue-900 flex gap-2 items-start" data-testid="notif-ios-install">
                <Info size={14} className="mt-0.5 shrink-0" />
                <div>
                  <b>iPhone / iPad:</b> notifications only work when this app is installed to your Home Screen. Tap the Share icon in Safari → <b>Add to Home Screen</b>, then open the app from your home screen and come back here to enable.
                </div>
              </div>
            )}
            {inIframe && (
              <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-[13px] text-amber-900 flex gap-2 items-start" data-testid="notif-iframe">
                <Info size={14} className="mt-0.5 shrink-0" />
                <div>
                  This page is running inside a frame — the browser won't show a permission prompt in frames. Open the app in its own tab, then try again.
                </div>
              </div>
            )}
            {supportsNotifications && !inIframe && permission === "denied" && (
              <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-[13px] text-red-900 flex gap-2 items-start" data-testid="notif-blocked">
                <Info size={14} className="mt-0.5 shrink-0" />
                <div>
                  <b>Blocked by {browserInfo.name}.</b> That's why clicking Enable does nothing — browsers won't re-prompt after you say No.<br />
                  <span className="text-red-800/80">{browserInfo.steps}</span>
                </div>
              </div>
            )}
            {supportsNotifications && !inIframe && permission === "default" && (
              <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-[13px] text-slate-700 flex gap-2 items-start">
                <Info size={14} className="mt-0.5 shrink-0" />
                <div>
                  Not asked yet — clicking <b>Enable notifications</b> will pop the browser's permission prompt. Tap <b>Allow</b> when it appears.
                </div>
              </div>
            )}

            <ul className="mt-4 text-[13px] text-slate-700 space-y-1.5" data-testid="settings-notif-events">
              <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-fuchsia-500" /> New open shifts on your facility</li>
              <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-fuchsia-500" /> PTO / trade / pickup / mod / availability decisions from your manager</li>
              <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-fuchsia-500" /> A shift offered to you (Open-Shift Manager)</li>
              <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-fuchsia-500" /> Shift starting in ~2 hours — includes time + department</li>
            </ul>
          </div>
        </div>
      </section>

      <AdminBootEveryoneSection />
    </div>
  );
}


// ---------------------------------------------------------------------------
// Admin-only — "Boot everyone" button. Bumps a server-side cutoff so every
// existing JWT is treated as revoked on the next request. The admin who
// clicks the button gets a fresh token in the response so they stay signed in.
function AdminBootEveryoneSection() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [lastCutoffIso, setLastCutoffIso] = useState(null);

  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/admin/global-logout-status")
      .then(({ data }) => setLastCutoffIso(data?.global_logout_at_iso || null))
      .catch(() => {});
  }, [user]);

  if (user?.role !== "admin") return null;

  const boot = async () => {
    const confirm1 = window.prompt(
      'This will FORCE-LOGOUT every user (including you — your session will be refreshed automatically). ' +
      'Type BOOT to confirm.'
    );
    if (confirm1 !== "BOOT") { toast.info("Cancelled — nothing was changed"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/boot-everyone", {});
      if (data?.token) localStorage.setItem("shiftly_token", data.token);
      setLastCutoffIso(data?.cutoff_iso || new Date().toISOString());
      toast.success("Everyone has been logged out. They'll be sent to the sign-in screen on their next action.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to boot users");
    } finally { setBusy(false); }
  };

  const lastHuman = lastCutoffIso ? new Date(lastCutoffIso).toLocaleString() : null;

  return (
    <section className="mt-8">
      <div className="flex items-center gap-2 mb-3">
        <ShieldAlert size={16} className="text-rose-600" />
        <h2 className="font-display font-semibold text-slate-900">Admin tools</h2>
      </div>
      <div className="rounded-2xl border border-rose-200 bg-rose-50/40 p-5 sm:p-6" data-testid="admin-boot-everyone-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <div className="font-semibold text-slate-900 flex items-center gap-2">
              <LogOut size={14} className="text-rose-600" /> Force sign-out all users
            </div>
            <p className="text-[13px] text-slate-600 mt-1 leading-snug">
              Logs every user out right now. Everyone's current session is revoked,
              so they'll land on the sign-in screen the next time they tap anything.
              Useful after bulk password resets, role switches, or account cleanup.
              Your own session will be refreshed automatically — you'll stay signed in.
            </p>
            {lastHuman && (
              <div className="text-[11px] text-slate-500 mt-2">
                Last used: <span className="tabular-nums font-medium">{lastHuman}</span>
              </div>
            )}
          </div>
          <Button
            variant="destructive"
            onClick={boot}
            disabled={busy}
            data-testid="admin-boot-everyone-btn"
            className="shrink-0"
          >
            <LogOut size={14} className="mr-1.5" />
            {busy ? "Booting…" : "Boot everyone out"}
          </Button>
        </div>
      </div>
    </section>
  );
}
